import {
  GRID_ROOT_GROUP_ID,
  type GridGroupNode,
  type GridLeafNode,
  type GridRowId,
  type GridRowTreeConfig,
  type GridDataSourceGroupNode,
} from '@mui/x-data-grid';
import type { GridTreeDepths } from '@mui/x-data-grid/internals';
import {
  updateGroupDefaultExpansion,
  checkGroupChildrenExpansion,
  getGroupRowIdFromPath,
  insertNodeInTree,
  updateGroupNodeIdAndAutoGenerated,
} from './utils';
import type { GridTreePathDuplicateHandler, RowTreeBuilderGroupingCriterion } from './models';
import type { DataGridProProps } from '../../models/dataGridProProps';

interface InsertDataRowInTreeParams {
  /**
   * ID of the data row to insert in the tree.
   */
  id: GridRowId;
  /**
   * Path of the data row to insert in the tree.
   */
  path: RowTreeBuilderGroupingCriterion[];
  /**
   * Tree in which to insert the data row.
   * This tree can be mutated but it's children should not.
   * For instance:
   *
   * - `tree[nodeId] = newNode` => valid
   * - `tree[nodeId].children.push(newNodeId)` => invalid
   */
  tree: GridRowTreeConfig;
  /**
   * Previous tree instance for comparison.
   */
  previousTree: GridRowTreeConfig | null;
  /**
   * Amount of nodes at each depth of the tree.
   * This object can be mutated.
   * For instance:
   *
   * - `treeDepths[nodeDepth] = treeDepth[nodeDepth] + 1` => valid
   */
  treeDepths: GridTreeDepths;
  /**
   * Callback fired when trying to insert a data row for a path already populated by another data row.
   */
  onDuplicatePath?: GridTreePathDuplicateHandler;
  isGroupExpandedByDefault?: DataGridProProps['isGroupExpandedByDefault'];
  defaultGroupingExpansionDepth: number;
  serverChildrenCount?: number;
  groupsToFetch?: Set<GridRowId>;
  maxDepth?: number;
}

/**
 * Inserts a data row in a tree.
 * For each steps of its path:
 * - if a node exists with the same partial path, it will register this node as the ancestor of the data row.
 * - if not, it will create an auto-generated node and register it as ancestor of the data row.
 */
export const insertDataRowInTree = ({
  id,
  path,
  previousTree,
  tree,
  treeDepths,
  onDuplicatePath,
  isGroupExpandedByDefault,
  defaultGroupingExpansionDepth,
  serverChildrenCount,
  groupsToFetch,
  maxDepth,
}: InsertDataRowInTreeParams) => {
  let parentNodeId = GRID_ROOT_GROUP_ID;

  for (let depth = 0; depth < path.length; depth += 1) {
    const { key, field } = path[depth];
    const fieldWithDefaultValue = field ?? '__no_field__';
    const keyWithDefaultValue = key ?? '__no_key__';
    const existingNodeIdWithPartialPath = (tree[parentNodeId] as GridGroupNode).childrenFromPath?.[
      fieldWithDefaultValue
    ]?.[keyWithDefaultValue.toString()];

    // When we reach the last step of the path,
    // We need to create a node for the row passed to `insertNodeInTree`
    if (depth === path.length - 1) {
      // If no node matches the full path,
      // We create a leaf node for the data row.
      if (existingNodeIdWithPartialPath == null) {
        let node: GridLeafNode | GridDataSourceGroupNode;
        if (serverChildrenCount !== undefined && serverChildrenCount !== 0) {
          node = {
            type: 'group',
            id,
            parent: parentNodeId,
            path: path.map((step) => step.key as string),
            depth,
            isAutoGenerated: false,
            groupingKey: key,
            groupingField: field,
            children: [],
            childrenFromPath: Object.create(null),
            childrenExpanded: false,
            serverChildrenCount,
          };
          const shouldFetchChildren = checkGroupChildrenExpansion(
            node,
            defaultGroupingExpansionDepth,
            maxDepth,
            isGroupExpandedByDefault,
          );
          if (shouldFetchChildren) {
            groupsToFetch?.add(id);
          }
        } else {
          node = {
            type: 'leaf',
            id,
            depth,
            parent: parentNodeId,
            groupingKey: key,
          };
        }

        insertNodeInTree(node, tree, treeDepths, previousTree);
      } else {
        const existingNodeWithPartialPath = tree[existingNodeIdWithPartialPath];

        // If we already have an auto-generated group matching the partial path,
        // We replace it with the node from of data row passed to `insertNodeInTree`
        if (
          existingNodeWithPartialPath.type === 'group' &&
          existingNodeWithPartialPath.isAutoGenerated
        ) {
          updateGroupNodeIdAndAutoGenerated({
            tree,
            previousTree,
            treeDepths,
            node: existingNodeWithPartialPath,
            updatedNode: {
              id,
              isAutoGenerated: false,
            },
          });
        } else {
          // If we have another row matching the partial path, then there is a duplicate in the dataset.
          // We warn the user and skip the current row.
          onDuplicatePath?.(existingNodeIdWithPartialPath, id, path);
        }
      }
    }
    // For the intermediary steps of the path,
    // We need to make sure that there is a node matching the partial path.
    //
    // If no node matches the partial path,
    // We create an auto-generated group node.
    else if (existingNodeIdWithPartialPath == null) {
      const nodeId = getGroupRowIdFromPath(path.slice(0, depth + 1));

      const autoGeneratedGroupNode: GridGroupNode = {
        type: 'group',
        id: nodeId,
        parent: parentNodeId,
        depth,
        isAutoGenerated: true,
        groupingKey: key,
        groupingField: field,
        children: [],
        childrenFromPath: Object.create(null),
        childrenExpanded: false,
      };

      insertNodeInTree(
        updateGroupDefaultExpansion(
          autoGeneratedGroupNode,
          defaultGroupingExpansionDepth,
          maxDepth,
          isGroupExpandedByDefault,
          (previousTree?.[nodeId] as GridGroupNode)?.childrenExpanded,
        ),
        tree,
        treeDepths,
        previousTree,
      );

      parentNodeId = nodeId;
    }
    // For the intermediary steps of the path
    // If a node matches the partial path, we use it as parent for the next step
    else {
      const currentGroupNode = tree[existingNodeIdWithPartialPath];

      // If the node matching the partial path is not a group, we turn it into a group
      if (currentGroupNode.type !== 'group') {
        const groupNode: GridGroupNode = {
          type: 'group',
          id: currentGroupNode.id,
          parent: currentGroupNode.parent,
          depth: currentGroupNode.depth,
          isAutoGenerated: false,
          groupingKey: key,
          groupingField: field,
          children: [],
          childrenFromPath: Object.create(null),
          childrenExpanded: false,
        };
        tree[existingNodeIdWithPartialPath] = updateGroupDefaultExpansion(
          groupNode,
          defaultGroupingExpansionDepth,
          maxDepth,
          isGroupExpandedByDefault,
          (previousTree?.[currentGroupNode.id] as GridGroupNode)?.childrenExpanded,
        );
      }
      parentNodeId = currentGroupNode.id;
    }
  }
};
